import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authApi, type LoginPayload } from "@/modules/auth/api/auth.api";
import { useAuthStore } from "@/modules/auth/store/auth.store";

/**
 * Auth hook — store + actions birlashtirilgan API.
 *
 * Komponentlar `const { user, login, logout, switchTenant } = useAuth()` qiladi.
 */
export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const currentTenant = useAuthStore((s) => s.currentTenant);
  const tenants = useAuthStore((s) => s.tenants);
  const isHydrating = useAuthStore((s) => s.isHydrating);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clear = useAuthStore((s) => s.clear);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: async (snapshot) => {
      setAuth(snapshot);
      const decopool = snapshot.tenants.find((t) => t.slug === "decopool");
      if (decopool && snapshot.currentTenant.id !== decopool.id) {
        try {
          const next = await authApi.switchTenant(decopool.id);
          setAuth(next);
        } catch {
          /* fallback — joriy tenantda qol */
        }
      }
      queryClient.invalidateQueries();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clear();
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  const switchTenantMutation = useMutation({
    mutationFn: (tenantId: string) => authApi.switchTenant(tenantId),
    onSuccess: (snapshot) => {
      setAuth(snapshot);
      queryClient.invalidateQueries();
      toast.success(`Workspace o'zgartirildi: ${snapshot.currentTenant.name}`);
    },
    onError: () => {
      toast.error("Workspace almashtirishda xato");
    },
  });

  const login = useCallback(
    (payload: LoginPayload) => loginMutation.mutateAsync(payload),
    [loginMutation],
  );

  const logout = useCallback(() => logoutMutation.mutate(), [logoutMutation]);

  const switchTenant = useCallback(
    (tenantId: string) => switchTenantMutation.mutateAsync(tenantId),
    [switchTenantMutation],
  );

  return {
    // holat
    isAuthenticated: Boolean(accessToken && user),
    isHydrating,
    user,
    currentTenant,
    tenants,
    // mutationlar
    login,
    logout,
    switchTenant,
    loginPending: loginMutation.isPending,
    loginError: loginMutation.error,
    switchPending: switchTenantMutation.isPending,
  };
}
